'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react'

import { loadExternalScript } from '@/components/integrations/shared/load-script'
import { intercomAppId } from '@/config/env.public'

type IntercomContextValue = {
  open: () => void
  /**
   * Hides or restores the launcher. Intercom draws it at a z-index nothing else can reach, so on a
   * phone it lands on top of the demo planner — a chat bubble over the calendar someone opened to
   * pick a date.
   */
  setLauncherVisible: (visible: boolean) => void
}

const IntercomContext = createContext<IntercomContextValue | null>(null)
const intercomScriptId = 'intercom-widget-script'

export function IntercomProvider({ children }: { children: ReactNode }) {
  const bootedRef = useRef(false)

  const ensureIntercomLoaded = useCallback(() => {
    if (typeof window === 'undefined') {
      return Promise.resolve()
    }

    window.intercomSettings = { app_id: intercomAppId, hide_default_launcher: false }

    if (window.Intercom && document.getElementById(intercomScriptId)) {
      return Promise.resolve()
    }

    return loadExternalScript({
      id: intercomScriptId,
      src: `https://widget.intercom.io/widget/${intercomAppId}`,
    })
  }, [])

  // The widget is ~300 KB of third-party JavaScript and the only external script on the critical
  // path. Nobody opens a chat in the first second of a page they have not read yet, so it waits
  // for a sign of life — a scroll, a pointer, a key — and for the browser to be idle. Anyone who
  // clicks a chat link before that gets it loaded on demand by `open()` below.
  useEffect(() => {
    let cancelled = false

    const boot = () => {
      if (cancelled) {
        return
      }
      void ensureIntercomLoaded()
        .then(() => {
          if (cancelled || !window.Intercom) {
            return
          }

          if (bootedRef.current) {
            window.Intercom('update', window.intercomSettings)
            return
          }

          window.Intercom('boot', window.intercomSettings)
          bootedRef.current = true
        })
        .catch(() => undefined)
    }

    const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const
    const start = () => {
      events.forEach((event) => window.removeEventListener(event, start))
      const idle = window.requestIdleCallback
      if (typeof idle === 'function') {
        idle(boot, { timeout: 2000 })
      } else {
        window.setTimeout(boot, 500)
      }
    }

    events.forEach((event) => window.addEventListener(event, start, { once: true, passive: true }))
    // A visitor who reads without touching anything still gets the launcher, just not at load.
    const fallback = window.setTimeout(start, 8000)

    return () => {
      cancelled = true
      window.clearTimeout(fallback)
      events.forEach((event) => window.removeEventListener(event, start))
    }
  }, [ensureIntercomLoaded])

  const value = useMemo(
    () => ({
      setLauncherVisible: (visible: boolean) => {
        if (!window.Intercom) {
          return
        }
        window.intercomSettings = { app_id: intercomAppId, hide_default_launcher: !visible }
        window.Intercom('update', window.intercomSettings)
      },
      open: () => {
        void ensureIntercomLoaded()
          .then(() => {
            if (!bootedRef.current && window.Intercom) {
              window.Intercom('boot', window.intercomSettings)
              bootedRef.current = true
            }
            window.Intercom?.('show')
          })
          .catch(() => undefined)
      },
    }),
    [ensureIntercomLoaded],
  )

  return <IntercomContext.Provider value={value} data-sentry-element="IntercomContext.Provider" data-sentry-component="IntercomProvider" data-sentry-source-file="intercom-provider.tsx">{children}</IntercomContext.Provider>
}

export function useIntercom() {
  const context = useContext(IntercomContext)
  if (!context) {
    throw new Error('useIntercom must be used within an IntercomProvider')
  }
  return context
}
